import '../pages.css';
import '../SenseShield.css';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import DateRangePicker, { computeRangeForPreset } from '../../components/Filters/DateRangePicker';
import FilterSelect from '../../components/Campaigns/FilterSelect';
import LatestExecutionsTable from '../../components/SenseShield/LatestExecutionsTable';
import { formatLocalDate, parseLocalDate } from '../../utils/dateFormat';

const TEST_STATUS_OPTIONS = [{ value: 'PASS' }, { value: 'FAIL' }, { value: 'WARNING' }];

function formatTestStatusLabel(value) {
  return value === 'WARNING' ? 'WARN' : value;
}

// ?from=YYYY-MM-DD&to=YYYY-MM-DD (calendar days, e.g. from the Summary page) -> picker range.
function rangeFromParams(searchParams) {
  const start = parseLocalDate(searchParams.get('from'));
  const end = parseLocalDate(searchParams.get('to'));
  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return null;
  return { startDate: start, endDate: end, preset: 'custom' };
}

function EvaluationRuns() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [deepLinkRunId, setDeepLinkRunId] = useState(() => searchParams.get('runId'));
  const [deepLinkPromptId, setDeepLinkPromptId] = useState(() => searchParams.get('promptId'));
  // A prompt-only link (?promptId=...) lists every run of that prompt in the period; a runId link
  // pins one exact run instead (see deepLinkRunId).
  const [promptFilter, setPromptFilter] = useState(() => (searchParams.get('runId') ? null : searchParams.get('promptId')));
  const [dateRange, setDateRange] = useState(
    () => rangeFromParams(searchParams) ?? { ...computeRangeForPreset('7d'), preset: '7d' }
  );
  const [promptTypeOptions, setPromptTypeOptions] = useState([]);
  const [selectedPromptTypes, setSelectedPromptTypes] = useState([]);
  const [selectedTestStatuses, setSelectedTestStatuses] = useState([]);
  const [failureCategoryOptions, setFailureCategoryOptions] = useState([]);
  const [selectedFailureCategories, setSelectedFailureCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const clearPromptFilter = useCallback(() => {
    setPromptFilter(null);
    navigate('/sense-shield/evaluation-runs', { replace: true });
  }, [navigate]);

  const clearDeepLink = useCallback(() => {
    setDeepLinkRunId(null);
    setDeepLinkPromptId(null);
    navigate('/sense-shield/evaluation-runs', { replace: true });
  }, [navigate]);

  useEffect(() => {
    api.get(`${QUALITY_SERVICE_API_URL}/hub/sense-shield/prompt-types`)
      .then((res) => setPromptTypeOptions(res.data.map((value) => ({ value }))))
      .catch(() => setPromptTypeOptions([]));
    api.get(`${QUALITY_SERVICE_API_URL}/hub/sense-shield/failure-categories`)
      .then((res) => setFailureCategoryOptions(res.data.map((value) => ({ value }))))
      .catch(() => setFailureCategoryOptions([]));
  }, []);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setDateRange((current) => ({ ...current }));
    setLoading(false);
  }, []);

  return (
    <div className="sense-shield-page">
      <div className="sense-shield-header">
        <h1>Evaluation Runs</h1>
        <div className="sense-shield-filters">
          <div className="sense-shield-filter-item">
            <label className="sense-shield-filter-label">Prompt Type</label>
            <FilterSelect options={promptTypeOptions} value={selectedPromptTypes} onChange={setSelectedPromptTypes} />
          </div>
          <div className="sense-shield-filter-item">
            <label className="sense-shield-filter-label">Test Result</label>
            <FilterSelect
              options={TEST_STATUS_OPTIONS}
              value={selectedTestStatuses}
              onChange={setSelectedTestStatuses}
              formatLabel={formatTestStatusLabel}
            />
          </div>
          <div className="sense-shield-filter-item">
            <label className="sense-shield-filter-label">Failure Category</label>
            <FilterSelect options={failureCategoryOptions} value={selectedFailureCategories} onChange={setSelectedFailureCategories} />
          </div>
          <DateRangePicker
            value={{ startDate: dateRange.startDate, endDate: dateRange.endDate }}
            preset={dateRange.preset}
            onChange={setDateRange}
            maxDate={new Date()}
            enabledPresets={['today', '7d', '14d', '30d', 'custom']}
          />
          <button className="sense-shield-refresh" onClick={handleRefresh} disabled={loading} type="button" aria-label="Refresh">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {deepLinkRunId && (
        <div className="sense-shield-deep-link-banner">
          <span>
            Showing run <strong>{deepLinkRunId}</strong>
            {deepLinkPromptId && <> · prompt <strong>{deepLinkPromptId}</strong></>}
          </span>
          <button type="button" className="sense-shield-deep-link-clear" onClick={clearDeepLink}>
            Clear
          </button>
        </div>
      )}

      {promptFilter && !deepLinkRunId && (
        <div className="sense-shield-deep-link-banner">
          <span>
            All runs of prompt <strong>{promptFilter}</strong> · {formatLocalDate(dateRange.startDate)} to {formatLocalDate(dateRange.endDate)}
          </span>
          <button type="button" className="sense-shield-deep-link-clear" onClick={clearPromptFilter}>
            Clear
          </button>
        </div>
      )}

      <div className="sense-shield-grid">
        <div className="sense-shield-panel sense-shield-panel-full">
          <LatestExecutionsTable
            dateRange={dateRange}
            promptTypes={selectedPromptTypes}
            testStatuses={selectedTestStatuses}
            failureCategories={selectedFailureCategories}
            promptId={promptFilter}
            deepLinkRunId={deepLinkRunId}
            deepLinkPromptId={deepLinkPromptId}
          />
        </div>
      </div>
    </div>
  );
}

export default EvaluationRuns;

